import { FC, PropsWithChildren, useEffect, useRef } from 'react';

import { initializeAnalytics, initializeIntercom } from '@bonusly/analytics';
import { useApplication } from '@bonusly/application';
import { useBreakpointValue } from '@bonusly/design-system';

import { useIsBizyEnabledForUser } from '../hooks/use-is-bizy-enabled-for-user';
import { initializeHotJar } from '../lib/hotjar';
import { useIdentityStore } from '../lib/store';
import { refreshToken } from '../lib/token';
import { useProfileQuery } from '../queries/profile';
import { createConfig } from '../utils/env';
import { joinPath } from '../utils/url';

interface AuthProviderProps extends PropsWithChildren {
	// Passing a JWT is intended for local development. This will bypass the need
	// to make a request with a cookie, which may not be available in a local dev.
	jwt?: string;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children, jwt: providedJWT }) => {
	const { application } = useApplication();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const initRef = useRef(false);
	const analyticsInitRef = useRef(false);

	// Read from Zustand — populated by the first effect below
	const isCompanyFetched = useIdentityStore((state) => state.isCompanyFetched);
	const isBizyEnabledForUser = useIsBizyEnabledForUser();

	useEffect(() => {
		if (initRef.current) return;
		initRef.current = true;

		(async () => {
			if (providedJWT) {
				useIdentityStore.setState({ token: providedJWT });
				return;
			}

			const jwt = await refreshToken();

			// If no JWT is available then the user is not logged in because of either an expired or missing session
			// token. In this case we need to manually redirect to the login page.
			if (!jwt) {
				const currentUrl = window.location.href;
				const redirectParam = encodeURIComponent(currentUrl);
				const loginURL = joinPath(application.recognizeOrigin, `users/sign_in_start?redirect_to=${redirectParam}`);

				window.location.assign(loginURL);

				return;
			}

			useIdentityStore.setState({ token: jwt });
		})();
	}, [application, providedJWT]);

	const { data } = useProfileQuery();

	// Write /me data to Zustand store so hooks/getters can read from it
	useEffect(() => {
		if (!data) return;

		const { company, features, user } = data;
		const config = createConfig(application);

		useIdentityStore.setState({
			...config,
			company,
			features,
			isCompanyFetched: true,
			isUserFetched: true,
			areFeaturesFetched: true,
			user,
		});
	}, [data, application]);

	// Initialize analytics once Zustand is populated so hook values are accurate.
	// isCompanyFetched transitions false → true after the effect above, which means
	// isBizyEnabledForUser is correct when this effect fires.
	useEffect(() => {
		if (!data || !isCompanyFetched || analyticsInitRef.current) return;

		analyticsInitRef.current = true;

		const { company, user } = data;
		const hideLauncher = isBizyEnabledForUser;

		initializeAnalytics({ company, user });
		initializeIntercom({ company, user, isMobile, hideLauncher });
		initializeHotJar({ company, user });
	}, [data, isCompanyFetched, isBizyEnabledForUser, isMobile]);

	return <>{children}</>;
};
